import { chunk, padStart, toString } from 'lodash';
import { Day, UnicodeEntity, WeekDay } from '@mews/enums';
import { WEEK_LENGTH } from './constants';
import { DateLocalization, DateStringFormatter, StructuredDate, StructuredMonth } from './types';

export default ({
    getLocalization,
    parseDateFromFormat,
}: {
    getLocalization: () => DateLocalization;
    parseDateFromFormat: (monthYearString: string, format: string, locale?: string) => DateStringFormatter;
}) => {
    const mod = (a: number, n: number): number => ((a % n) + n) % n;

    const addMonths = ({ month, year }: StructuredMonth, count: number): StructuredMonth => {
        const newMonth = mod(Number(month) + count, 12);
        const newYear = Number(year) + Math.floor((Number(month) + count) / 12);

        return {
            year: newYear,
            month: newMonth,
        };
    };

    const getDaysOfMonth = (year: number, month: number): number => new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

    const getMonthOfDate = ({ year, month }: StructuredDate): StructuredMonth => ({
        year,
        month,
    });

    const subtractMonths = (month: StructuredMonth, count: number): StructuredMonth => addMonths(month, -count);
    const getNextMonth = (month: StructuredMonth): StructuredMonth => addMonths(month, 1);
    const getPrevMonth = (month: StructuredMonth): StructuredMonth => addMonths(month, -1);

    const isMonthEqual = (month1: StructuredMonth, month2: StructuredMonth): boolean =>
        month1.year === month2.year && month1.month === month2.month;

    const isMonthGreater = (month1: StructuredMonth, month2: StructuredMonth): boolean =>
        month1.year > month2.year || (month1.year === month2.year && month1.month > month2.month);

    const isMonthLess = (month1: StructuredMonth, month2: StructuredMonth): boolean =>
        month1.year < month2.year || (month1.year === month2.year && month1.month < month2.month);

    const isMonthDisabled = (
        month: StructuredMonth,
        { minDate, maxDate }: { minDate?: StructuredDate; maxDate?: StructuredDate } = {}
    ): boolean =>
        (minDate && isMonthLess(month, { month: minDate.month, year: minDate.year })) ||
        (maxDate && isMonthGreater(month, { month: maxDate.month, year: maxDate.year })) ||
        false;

    const isFutureMonthDisabled = (
        month: StructuredMonth,
        { maxDate }: { minDate?: StructuredDate; maxDate?: StructuredDate } = {}
    ): boolean =>
        (maxDate &&
            (isMonthLess({ month: maxDate.month, year: maxDate.year }, month) ||
                isMonthEqual({ month: maxDate.month, year: maxDate.year }, month))) ||
        false;

    const isPastMonthDisabled = (
        month: StructuredMonth,
        { minDate }: { minDate?: StructuredDate; maxDate?: StructuredDate } = {}
    ): boolean =>
        (minDate &&
            (isMonthGreater({ month: minDate.month, year: minDate.year }, month) ||
                isMonthEqual({ month: minDate.month, year: minDate.year }, month))) ||
        false;

    const diffMonths = (month1: StructuredMonth, month2: StructuredMonth): number =>
        12 * (month2.year - month1.year) + (month2.month - month1.month);

    const getMonthIndex = ({ month }: StructuredMonth): number => month;

    const getMonthName = ({ month, year }: StructuredMonth): string => {
        const monthName = getLocalization().monthNames[month];
        return `${monthName} ${year}`;
    };
    const getMonthShortName = ({ month }: StructuredMonth): string => getLocalization().monthShortNames[month];

    const getMonthShortNames = (): Array<string> => getLocalization().monthShortNames;

    const getMonthUniqueName = ({ year, month }: StructuredMonth): string => `${year}-${month}`;

    const getMonthFirstDate = ({ year, month }: StructuredMonth): StructuredDate => ({
        date: 1,
        month,
        year,
    });

    const getMonthLastDate = ({ year, month }: StructuredMonth): StructuredDate => ({
        date: getDaysOfMonth(year, month),
        month,
        year,
    });

    const getMonthYearString = ({ year, month }: StructuredMonth): string => {
        const monthString = padStart(toString(month + 1), 2, '0');
        return `${monthString}${UnicodeEntity.ForwardSlash}${year}`;
    };

    const getDateStringFromMonthYearString = (monthYearString: string, dateFormatPattern: string): string => {
        const { cultureCode: locale, dateFormat } = getLocalization();

        // generate a localized valid date from the month and year string
        const date = parseDateFromFormat(monthYearString, dateFormatPattern, locale);

        if (!date.isValid) {
            return '';
        }

        return date.toFormat(dateFormat);
    };

    const getWeeksOfMonth = ({ year, month }: StructuredMonth): Array<Array<StructuredDate>> => {
        const startDate = new Date(Date.UTC(year, month, 1));
        const { weekStart } = getLocalization();
        const dayCount = getDaysOfMonth(year, month);
        const { value: weekStartDayNumber } = Day.getByName(weekStart as WeekDay);

        const dateRange: StructuredDate[] = [];
        for (let i = 1; i <= dayCount; i++) {
            dateRange.push({
                year,
                month,
                date: i,
            });
        }

        const startDay = mod(startDate.getUTCDay() - weekStartDayNumber, WEEK_LENGTH);
        const startDayOffset = mod(startDay, WEEK_LENGTH);
        let endDayOffset = mod(WEEK_LENGTH - (startDayOffset + dayCount), WEEK_LENGTH);

        const prevMonth = getPrevMonth({ year, month });
        const prevMonthNumberOfDays = getDaysOfMonth(prevMonth.year, prevMonth.month);
        const prevMonthDays: StructuredDate[] = [];
        for (let i = prevMonthNumberOfDays; i > prevMonthNumberOfDays - startDayOffset; i--) {
            prevMonthDays.unshift({
                year: prevMonth.year,
                month: prevMonth.month,
                date: i,
            });
        }

        const numberOfRows = Math.ceil((startDayOffset + dateRange.length) / WEEK_LENGTH);
        // making sure that the number of rows in calendar is always 6
        if (numberOfRows === 4) {
            endDayOffset += 2 * WEEK_LENGTH;
        }
        if (numberOfRows === 5) {
            endDayOffset += WEEK_LENGTH;
        }

        const nextMonth = getNextMonth({ year, month });
        const nextMonthDays: StructuredDate[] = [];
        for (let i = 1; i <= endDayOffset; i++) {
            nextMonthDays.push({
                year: nextMonth.year,
                month: nextMonth.month,
                date: i,
            });
        }

        const normalizedRange = [...prevMonthDays, ...dateRange, ...nextMonthDays];

        return chunk(normalizedRange, WEEK_LENGTH);
    };

    return {
        addMonths,
        getMonthOfDate,
        getMonthFirstDate,
        getMonthLastDate,
        subtractMonths,
        getNextMonth,
        getPrevMonth,
        isMonthDisabled,
        isFutureMonthDisabled,
        isPastMonthDisabled,
        isMonthEqual,
        isMonthGreater,
        isMonthLess,
        diffMonths,
        getMonthIndex,
        getMonthName,
        getMonthYearString,
        getDateStringFromMonthYearString,
        getMonthShortName,
        getMonthShortNames,
        getMonthUniqueName,
        getWeeksOfMonth,
    };
};
